// NodeMissionTaskAttach.jsx

import React, { useState, useRef } from 'react';

// Hooks
import useActorCapabilities from 'hooks/useActorCapabilities';
import useNodeContributionHandlers from 'node/hooks/useNodeContributionHandlers';
import useNodeContributionState from 'node/hooks/useNodeContributionState';

// Utilities
import { canUser, PermissionAction } from 'utilities/NodeSecurity';

// CSS
import './NodeMissionTaskAttach.css';

// Components
import CameraModal from 'editor/nodeadd/camera/CameraModal';
import NodeAddRelatedButton from 'node/addrelated/NodeAddRelatedButton';
import NodeHiddenFileInputs from 'node/inputs/NodeHiddenFileInputs';

/**
 * NodeMissionTaskAttach
 *
 * Attach related records to a workflow step TASK without leaving the Workflow tab.
 * Related nodes render in NodeMissionTaskAttachments.
 *
 * @param {object} stepNode - Linked TASK node for the people step.
 * @param {object|null} user - Current signed-in user.
 * @returns {ReactNode}
 */
const NodeMissionTaskAttach = React.memo(({
  stepNode,
  user = null,
}) => {
  const { canEdit } = useActorCapabilities();

  const [, setDefaultNode] = useState(null);
  const [, setEditorSaveMode] = useState(null);
  const [, setInitialChildren] = useState([]);
  const [, setInitialActiveNodeKey] = useState(null);
  const [, setIsUploading] = useState(false);

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const filesInputRef = useRef(null);

  const {
    cameraOpen,
    cameraContentType,
    contributeParent,
    setContributeOpen,
    setContributeParent,
    setPreSelectedType,
    setCameraOpen,
    setCameraContentType,
  } = useNodeContributionState();

  const canAttach = Boolean(canEdit)
    && Boolean(stepNode?.key)
    && canUser(PermissionAction.EDIT, stepNode, user);

  const {
    handleQuickActionSelect,
    handlePhotoSelect,
    handleVideoSelect,
    handleDocumentSelect,
    handleFilesSelect,
    handleCameraContentCreated,
  } = useNodeContributionHandlers({
    node: stepNode,
    user,
    navigate: null,
    setCurrentNode: null,
    setCurrentSection: null,
    contributeParent,
    setContributeOpen,
    setContributeParent,
    setPreSelectedType,
    setCameraOpen,
    setCameraContentType,
    setDefaultNode,
    setEditorSaveMode,
    setInitialChildren,
    setInitialActiveNodeKey,
    setIsUploading,
    photoInputRef,
    videoInputRef,
    documentInputRef,
    filesInputRef,
  });

  if (!stepNode?.key || !canAttach) {
    return null;
  }

  return (
    <div className="node-mission-task-attach">
      <NodeAddRelatedButton
        asButton
        iconKey="attach_file"
        label="Attach"
        node={stepNode}
        showImport={false}
        onQuickActionSelect={handleQuickActionSelect}
      />

      <NodeHiddenFileInputs
        photoInputRef={photoInputRef}
        videoInputRef={videoInputRef}
        documentInputRef={documentInputRef}
        filesInputRef={filesInputRef}
        onDocumentSelect={handleDocumentSelect}
        onFilesSelect={handleFilesSelect}
        onPhotoSelect={handlePhotoSelect}
        onVideoSelect={handleVideoSelect}
      />

      <CameraModal
        isOpen={cameraOpen}
        setOpen={setCameraOpen}
        contentType={cameraContentType}
        onContentCreated={handleCameraContentCreated}
      />
    </div>
  );
});

NodeMissionTaskAttach.displayName = 'NodeMissionTaskAttach';

export default NodeMissionTaskAttach;
